import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowShareCalendar, setHelpMenu } from '../Redux/store';
import { supabase } from '../supabase/client';
import { X, UserPlus, ArrowRight, MessageCircle } from 'lucide-react';
import ModalWrapper from '../components/ModalWrapper';
import ConfirmDialog from '../components/ConfirmDialog';
import ProfileRowShare from '../components/ProfileRowShare';
import UserSearch from '../components/UserSearch';
import ShareCodeDisplay from '../components/ShareCodeDisplay';
import HelpButton from '../components/HelpButton';
import { sharedTo, sharedFrom } from '../data/functions';

const TAG = '[ShareCalendarOverlay]';

/** Full-screen overlay to share your calendar with contacts */
function ShareCalendarOverlay() {
  const open = useSelector((s) => s.ui.showShareCalendar);
  const meId = useSelector((s) => s.user.authUserId);
  const ownedShares = useSelector((s) => s.calendarShares.ownedShares);
  const receivedShares = useSelector((s) => s.calendarShares.receivedShares);
  const dispatch = useDispatch();

  const [confirmUnshare, setConfirmUnshare] = useState(null); // stores the entry to unshare
  const [shareMessage, setShareMessage] = useState(null); // { text: string, style: string }
  const [sharing, setSharing] = useState(false); // loading state to prevent double-clicks
  const user = useSelector((s) => s.user.profile);
  const userRole = user?.role ?? 'ip';

  // Auto-show share_welcome on first open
  useEffect(() => {
    if (open && user) {
      const firstTimeViews = user?.first_time_views || {};
      if (!firstTimeViews.share_welcome) {
        dispatch(setHelpMenu('share_welcome'));
      }
    }
  }, [open, user, dispatch]);

  const handleShare = async (user) => {
    if (!meId) return;
    if (sharing) return;
    setSharing(true);
    setShareMessage(null);
    // Send message with share attachment via ensure_chat_and_send_j
    const result = await supabase.rpc('ensure_chat_and_send_j', {
      p_user_id1: meId,
      p_user_id2: user.id,
      p_message_json: {
        sender_id: meId,
        body: 'Calendar Shared',
        image_urls: null,
        attachments: [{
          type: 'calendar_share',
          data: {
            requesterId: meId
          }
        }]
      }
    });
    // No calendar_shares insert - happens when recipient accepts
    if (result.error) {
      setShareMessage({ text: 'Error sharing calendar. Please try again.', style: 'text-red-500' });
    } else {
      setShareMessage({ text: 'Share Sent! Other user can accept in chat', style: 'text-green-500' });
    }
    setSharing(false);
  };

  const handleRequest = async (user) => {
    if (!meId) return;
    if (sharing) return;
    setSharing(true);
    setShareMessage(null);
    // Send message with request attachment via ensure_chat_and_send_j
    const { data, error } = await supabase.rpc('ensure_chat_and_send_j', {
      p_user_id1: meId,
      p_user_id2: user.id,
      p_message_json: {
        sender_id: meId,
        body: 'Calendar Request',
        image_urls: null,
        attachments: [{
          type: 'calendar_request',
          data: {
            requesterId: meId
          }
        }]
      }
    });
    if (error) {
      console.error('[ShareCalendarOverlay] ensure_chat_and_send_j error:', error);
      setShareMessage({ text: 'Error requesting calendar. Please try again.', style: 'text-red-500' });
    } else {
      setShareMessage({ text: 'Request Sent! Other user can accept in chat', style: 'text-green-500' });
    }
    // No calendar_shares insert - happens when recipient accepts
    setSharing(false);
  };

  const handleUnshare = async () => {
    if (!meId || !confirmUnshare) {
      return;
    }
    
    try {
      // Delete the share - RLS will handle whether user can delete
      const { error, data } = await supabase.from('calendar_shares').delete().eq('id', confirmUnshare.shareId).select();
      
      if (error) {
        console.error(`${TAG} handleUnshare - delete error:`, error);
        throw error;
      }
      setConfirmUnshare(null);
    } catch (err) {
      console.error(`${TAG} handleUnshare - catch error:`, err);
    }
  };

  if (!open) return null;

  return (
    <ModalWrapper onClose={() => dispatch(setShowShareCalendar(false))}>
      {/* Header */}
      <header className="relative flex h-14 shrink-0 items-center justify-center border-b border-border px-4">
        <div className="absolute left-4">
          <HelpButton helpKey="share" />
        </div>
        <h2 className="text-base font-semibold">Calendar Sharing</h2>
        <button onClick={() => dispatch(setShowShareCalendar(false))} className="absolute right-4">
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
        {/* Quick steps guide */}
        <div className="rounded-xl border-2 border-primary bg-gradient-to-t from-accent to-primary p-4 space-y-3">
          <div className="flex items-center justify-center gap-2 mb-2">
            <p className="text-base font-bold text-white text-center">2 ways to share your calendar</p>
          </div>

          {/* Option 1: Share your calendar */}
          <div className="rounded-lg bg-muted/50 p-2.5 relative">
            <div className="absolute top-1.5 left-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
              1
            </div>
            <div className="flex-1 pl-8">
              <h3 className="text-xs font-semibold text-foreground mb-1">Share your calendar</h3>
              <div className="text-sm text-foreground space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</div>
                  <p>Type a partners share code into the box below</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</div>
                  <p>Click share</p>
                </div>
              </div>
            </div>
          </div>

          {/* Option 2: Request a calendar */}
          <div className="rounded-lg bg-muted/50 p-2.5 relative">
            <div className="absolute top-1.5 left-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
              2
            </div>
            <div className="flex-1 pl-8">
              <h3 className="text-xs font-semibold text-foreground mb-1">Request a calendar</h3>
              <div className="text-sm text-foreground space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</div>
                  <p>copy your share code</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</div>
                  <p>send it to a partner</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">3</div>
                  <p>watch your chats for the invite</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search contacts */}
        <div className="rounded-xl border-2 border-primary bg-gradient-to-t from-accent to-primary p-4 space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-white text-center">
            Search Someone to Share or Request
          </label>
          <UserSearch
            onSelected={(user) => {
              // Optional: handle selection logic if needed
            }}
            rowButtons={[
              // IP users only see Share button, donor users only see Request button
              ...(userRole === 'ip' ? [{
                icon: ArrowRight,
                label: 'Share',
                onClick: handleShare
              }] : [{
                icon: UserPlus,
                label: 'Request',
                onClick: handleRequest
              }])
            ]}
          />

          {/* Share/Request Status Message */}
          {shareMessage && (
            <div className="text-center text-sm font-medium">
              <p className={shareMessage.style}>{shareMessage.text}</p>
            </div>
          )}
        </div>

        {/* My Share Code */}
        <ShareCodeDisplay />


        {/* Viewing - user is shared_with */}
        <div className="space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-primary">
            Viewing
          </label>
          {receivedShares && Object.keys(receivedShares).length > 0 ? (
            <ul className="space-y-2">
              {Object.values(receivedShares).map((share) => (
                <li key={share.id} className="rounded-2xl border border-border bg-card px-3 py-2.5">
                  <ProfileRowShare
                    share={share}
                    meId={meId}
                    onDelete={(confirmData) => setConfirmUnshare(confirmData)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">
                You aren't viewing anyone's calendar yet.
              </p>
            </div>
          )}
        </div>

        {/* Shared To - user is owner */}
        <div className="space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-primary">
            Shared To
          </label>
          {ownedShares && Object.keys(ownedShares).length > 0 ? (
            <ul className="space-y-2">
              {Object.values(ownedShares).map((share) => (
                <li key={share.id} className="rounded-2xl border border-border bg-card px-3 py-2.5">
                  <ProfileRowShare
                    share={share}
                    meId={meId}
                    onDelete={(confirmData) => setConfirmUnshare(confirmData)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">
                You haven't shared your calendar with anyone yet.
              </p>
            </div>
          )}
        </div>

      </div>

      {confirmUnshare && (
        <ConfirmDialog
          message={confirmUnshare.message}
          confirmLabel="Unshare"
          cancelLabel="Keep Sharing"
          destructive={true}
          onConfirm={handleUnshare}
          onCancel={() => setConfirmUnshare(null)}
        />
      )}
    </ModalWrapper>
  );
}

export default ShareCalendarOverlay;
